import { Navigate, Outlet, type RouteObject } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { Brand } from '@/components/Brand'
import { Spinner } from '@/components/ui/primitives'
import { ToastProvider } from '@/components/ui/toast'
import { AuthProvider, useAuth } from '@/lib/auth'
import { CountryProvider } from '@/lib/country'
import { AcceptQuotation } from '@/routes/AcceptQuotation'
import { AdConnections } from '@/routes/AdConnections'
import { Analytics } from '@/routes/Analytics'
import { Approvals } from '@/routes/Approvals'
import { ApprovalChainDetailPage, ApprovalChains } from '@/routes/ApprovalChains'
import { CountryDetailPage, Countries } from '@/routes/Countries'
import { ReferenceLists } from '@/routes/ReferenceLists'
import { Customers, CustomerDetailPage } from '@/routes/Customers'
import { Dashboard } from '@/routes/Dashboard'
import { LeadDetailPage, Leads } from '@/routes/Leads'
import { LeadFormDetailPage, LeadForms } from '@/routes/LeadForms'
import { EmailTemplates } from '@/routes/EmailTemplates'
import { NotFound } from '@/routes/NotFound'
import { Notifications } from '@/routes/Notifications'
import { ProductDetailPage, Products } from '@/routes/Products'
import { PublicLeadForm } from '@/routes/PublicLeadForm'
import { QuotationBuilder } from '@/routes/QuotationBuilder'
import { Quotations } from '@/routes/Quotations'
import { Commission, StatementPage } from '@/routes/Commission'
import { ResellerDetailPage, Resellers } from '@/routes/Resellers'
import { SignedOut } from '@/routes/SignedOut'
import { SignIn } from '@/routes/SignIn'
import { TeamDetailPage, Teams } from '@/routes/Teams'
import { Terms } from '@/routes/Terms'
import { Users } from '@/routes/Users'
import { VerifyAcceptance } from '@/routes/VerifyAcceptance'

/**
 * The application's routes, as a tree a data router can be built from.
 *
 * Declared rather than rendered because the router has to be a data router: it is the
 * only kind that supports `useBlocker`, and that hook is what stops somebody walking away
 * from a half-edited form. See `useLeaveGuard`.
 *
 * The shape mirrors the questions asked in order - are the providers up, has the session
 * been restored, is anybody signed in - so each answer lives in exactly one layout route
 * rather than in early returns scattered through a component.
 */
export const routes: RouteObject[] = [
  {
    element: <Providers />,
    children: [
      // Outside Booting and outside RequireAuth, deliberately. A customer has no ARMS
      // account and never will, so this must not wait on a session being restored and must
      // never be bounced to sign-in. See docs/adr/0004-customer-acceptance-link.md.
      { path: 'accept/:token', element: <AcceptQuotation /> },

      // The second anonymous page, and for the same reason as the first: the person checking
      // a reference is the customer's auditor or their bank, who have no account and never
      // will. Both forms are routed - with a reference, and the box to type one into.
      { path: 'verify', element: <VerifyAcceptance /> },
      { path: 'verify/:reference', element: <VerifyAcceptance /> },

      // The third: a contact form marketing published. Whoever fills it in is a stranger
      // to every system here, and the page is Codemax's, never QuoteHero's.
      { path: 'f/:slug', element: <PublicLeadForm /> },
      {
        element: <Booting />,
        children: [
          {
            element: <PublicOnly />,
            children: [
              { path: 'sign-in', element: <SignIn /> },
              { path: 'signed-out', element: <SignedOut /> },
            ],
          },
          {
            element: <RequireAuth />,
            children: [
              {
                element: <AppShell />,
                children: [
                  { index: true, element: <Dashboard /> },
                  { path: 'quotations', element: <Quotations /> },
                  { path: 'quotations/:id', element: <QuotationBuilder /> },
                  { path: 'approvals', element: <Approvals /> },
                  { path: 'notifications', element: <Notifications /> },
                  { path: 'analytics', element: <Analytics /> },
                  { path: 'commission', element: <Commission /> },
                  { path: 'commission/:id', element: <StatementPage /> },
                  { path: 'leads', element: <Leads /> },
                  { path: 'leads/:id', element: <LeadDetailPage /> },
                  { path: 'customers', element: <Customers /> },
                  { path: 'customers/:id', element: <CustomerDetailPage /> },
                  { path: 'products', element: <Products /> },
                  { path: 'products/:id', element: <ProductDetailPage /> },
                  { path: 'resellers', element: <Resellers /> },
                  { path: 'resellers/:id', element: <ResellerDetailPage /> },
                  { path: 'countries', element: <Countries /> },
                  { path: 'countries/:id', element: <CountryDetailPage /> },
                  { path: 'approval-chains', element: <ApprovalChains /> },
                  { path: 'approval-chains/:id', element: <ApprovalChainDetailPage /> },
                  { path: 'lists', element: <ReferenceLists /> },
                  { path: 'terms', element: <Terms /> },
                  { path: 'email-templates', element: <EmailTemplates /> },
                  { path: 'lead-forms', element: <LeadForms /> },
                  { path: 'lead-forms/:id', element: <LeadFormDetailPage /> },
                  { path: 'lead-sources', element: <AdConnections /> },
                  { path: 'users', element: <Users /> },
                  { path: 'teams', element: <Teams /> },
                  { path: 'teams/:id', element: <TeamDetailPage /> },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]

/**
 * Everything every screen needs, in the order it depends on.
 *
 * A layout route rather than a wrapper around the router, because AuthProvider calls
 * useNavigate and a data router only provides that to what it renders.
 */
function Providers() {
  return (
    <ToastProvider>
      <AuthProvider>
        {/* Country depends on the signed-in user's grants, so it nests inside auth. */}
        <CountryProvider>
          <Outlet />
        </CountryProvider>
      </AuthProvider>
    </ToastProvider>
  )
}

/**
 * Nothing renders until the stored session has been checked. Without this the sign-in
 * screen flashes on every reload for somebody who is already signed in.
 *
 * A bare spinner on an empty page is indistinguishable from a page that failed to load,
 * so this says which application is starting and that it is still working.
 */
function Booting() {
  const { restoring } = useAuth()

  if (!restoring) return <Outlet />

  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="flex flex-col items-center gap-6">
        <Brand className="h-9" />

        <div role="status" className="flex items-center gap-2.5 text-muted">
          <Spinner className="size-4" label={null} />
          <span className="text-[13px]">Loading...</span>
        </div>
      </div>
    </div>
  )
}

function RequireAuth() {
  const { user } = useAuth()

  return user ? <Outlet /> : <Navigate to="/sign-in" replace />
}

/** The two screens that only mean something to somebody who is not signed in. */
function PublicOnly() {
  const { user } = useAuth()

  return user ? <Navigate to="/" replace /> : <Outlet />
}
